import { useState } from 'react';
import { FormDialog } from '@nyce/admin-kit/console';
import { Button, TextField } from '@nyce/admin-kit/ui';
import { ConfirmButton } from '../../components/common';
import { stats, type Installation } from '../../api/stats';
import { formatDateTime } from '../../lib/format';

type Kind = 'inactive' | 'ignored';

const set = (inst: Installation, kind: Kind, on: boolean, note?: string) => {
  const target = { customerId: inst.customerId, workserver: inst.workserver, note };
  return kind === 'inactive'
    ? stats.setInstallationInactive({ ...target, inactive: on })
    : stats.setInstallationIgnored({ ...target, ignored: on });
};

const DIALOG: Record<Kind, { title: string; submit: string; placeholder: string; hint: string }> = {
  inactive: {
    title: 'inactive',
    submit: 'Mark inactive',
    placeholder: 'Decommissioned, replaced by …',
    hint: "It no longer counts as silent and is left out of its organization's status. As soon as it sends any data again, it is active again by itself.",
  },
  ignored: {
    title: 'not monitored',
    submit: "Don't monitor",
    placeholder: 'Acceptance environment, used now and then …',
    hint: "For test and acceptance WorkServers that are quiet for days: never silent, left out of its organization's status and the fleet-wide regressions. Its data is still collected and shown. It stays until someone monitors it again.",
  },
};

/**
 * An installation's operator marks — inactive (ends when it sends data again) and not monitored (stays until cleared):
 * who set them and why, and for stats admins the buttons to set or clear them. "Mark inactive" is offered for silent
 * and never-seen installations only (one that still sends data would end it within a minute); "Don't monitor" for any.
 */
export function InstallationMarks({
  inst,
  canAdmin,
  onChanged,
  onError,
}: {
  inst: Installation;
  canAdmin: boolean;
  onChanged: (next: Installation) => void;
  onError: (e: unknown) => void;
}) {
  const [open, setOpen] = useState<Kind | null>(null);
  const [busy, setBusy] = useState(false);
  const inactive = inst.status === 'inactive' ? inst.inactive : null;
  const ignored = inst.ignored ?? null;

  const clear = async (kind: Kind) => {
    setBusy(true);
    try {
      onChanged(await set(inst, kind, false));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="sx-inactive" data-testid="installation-marks">
      {ignored && (
        <span className="nyk-ui-hint">
          Not monitored since {formatDateTime(ignored.since)} by {ignored.by}
          {ignored.note && `: ${ignored.note}`}.
        </span>
      )}
      {inactive && (
        <span className="nyk-ui-hint">
          Marked inactive {formatDateTime(inactive.since)} by {inactive.by}
          {inactive.note && `: ${inactive.note}`}. Ends when it sends data again.
        </span>
      )}
      {canAdmin && ignored && (
        <ConfirmButton
          size="sm"
          variant="secondary"
          loading={busy}
          message={`Monitor ${inst.workserver} again? It counts as silent again while it sends nothing.`}
          onConfirm={() => void clear('ignored')}
        >
          Monitor
        </ConfirmButton>
      )}
      {canAdmin && !ignored && inactive && (
        <ConfirmButton
          size="sm"
          variant="secondary"
          loading={busy}
          message={`Mark ${inst.workserver} active? It counts as silent again while it sends nothing.`}
          onConfirm={() => void clear('inactive')}
        >
          Mark active
        </ConfirmButton>
      )}
      {canAdmin && !ignored && (inst.status === 'silent' || inst.status === 'never') && (
        <Button size="sm" variant="secondary" onClick={() => setOpen('inactive')}>
          Mark inactive
        </Button>
      )}
      {canAdmin && !ignored && (
        <Button size="sm" variant="secondary" onClick={() => setOpen('ignored')}>
          Don't monitor
        </Button>
      )}
      {open && (
        <MarkDialog
          inst={inst}
          kind={open}
          onClose={() => setOpen(null)}
          onDone={(next) => {
            setOpen(null);
            onChanged(next);
          }}
        />
      )}
    </span>
  );
}

function MarkDialog({
  inst,
  kind,
  onClose,
  onDone,
}: {
  inst: Installation;
  kind: Kind;
  onClose: () => void;
  onDone: (next: Installation) => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const d = DIALOG[kind];
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await set(inst, kind, true, note.trim() || undefined));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <FormDialog
      onClose={onClose}
      title={`Mark ${inst.workserver} ${d.title}`}
      submitLabel={d.submit}
      busy={busy}
      error={error}
      onSubmit={() => void send()}
      formClassName="sx-form"
    >
      <TextField
        label="Note (optional)"
        name="note"
        maxLength={500}
        placeholder={d.placeholder}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <p className="nyk-ui-hint">{d.hint}</p>
    </FormDialog>
  );
}
